import React from 'react';

// VipBoardCard — the condo TV board's VIP card, on the desktop dashboard.
//
// Greg asked for this "in the style that kiosk shows it", 7-day graph included, so
// this is a faithful port of the VIP block in public/tv.html: the net-change bars
// with their caption, then the navy strip carrying Renewing VIPs / Active VIPs.
//
// ⚠️ IT READS THE BOARD'S OWN ENDPOINT (kiosk-dashboard), not a second query.
// That function already takes a live CPx staff session as one of its two doors, so
// the desktop can ask it directly — which means the wall and the desktop cannot
// print different numbers, the whole point of the 2026-08-26 fix that started this.
// Only the RENDERING is duplicated here, because tv.html is vanilla DOM and this is
// React; if the geometry below changes, change it there too.
//
// The kiosk's hardcoded hex values are the brand palette by another name
// (#00395C = --navy, #DC582A = --orange, #9aa0a6 = --gray-500, #dadce0 = --gray-300),
// so the port uses the variables and comes out pixel-identical.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtN = (n) => (n == null ? '–' : Number(n).toLocaleString());

// A YYYY-MM-DD string, parsed as a LOCAL date. new Date('2026-08-26') is parsed as
// UTC midnight and renders the day before in Eastern — the same trap the kiosk
// endpoint avoids with to_char on the SQL side.
function dowLabel(ymd) {
  const p = String(ymd || '').split('-');
  if (p.length !== 3) return '';
  return DOW[new Date(+p[0], +p[1] - 1, +p[2]).getDay()];
}

// "Today" for the live bar, "Sat 22" for the rest - the same labels the wall uses.
// Without the Today case the last bar carried a weekday name and read as a finished
// day, which is exactly the confusion the TV was fixed for on 2026-08-27.
function trendLabel(ymd, partial) {
  if (partial) return 'Today';
  const p = String(ymd || '').split('-');
  if (p.length !== 3) return '';
  return dowLabel(ymd) + ' ' + Number(p[2]);
}

// The caption carries the WINDOW TOTALS, not a restatement of what the bars plainly
// are (Greg, 2026-08-27: "just simply add in the totals for the last seven days").
// Summed from the very array the bars are drawn from, so the row and the chart cannot
// disagree, and deliberately NOT from the server's not_renewing_change_7d.
//
// ⚠️ The whole clause is suppressed unless every counted day carried a split that
// reconciled. kiosk-dashboard nulls a gained/opted_out pair rather than emit one that
// does not add up, and three numbers printed side by side WILL be checked for closing
// - a total silently short by one day is worse than no total.
//
// "gained", never "new": it is the change in ACTIVE members, so signups net of
// expiries. The two match only in a week where nothing lapsed; in April, when ~18% of
// the base expires at once, this goes hundreds negative while real signups stay
// positive, and a row reading "−800 new" would be nonsense.
function trendCaption(days, known) {
  let netTot = 0, gainTot = 0, outTot = 0, inTot = 0, splitCloses = true;
  for (const d of days) {
    if (!d || d.delta == null) continue;
    netTot += d.delta;
    if (d.gained == null || d.opted_out == null) { splitCloses = false; continue; }
    gainTot += d.gained;
    outTot += d.opted_out;
    inTot += Number(d.opted_in || 0);
  }
  const sgn = (v) => (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v);
  // Counted from the days actually measured: claiming seven when the snapshot cron
  // missed one would overstate the window these totals cover.
  const span = `last ${known} day${known === 1 ? '' : 's'}`;
  const outTxt = outTot < 0 ? `${Math.abs(outTot)} opted back in` : `${outTot} opted out`;
  // Named for the same reason the wall names it: without it "gained" exceeds "signed
  // up" with nothing on screen accounting for the difference. See public/tv.html.
  const inTxt = inTot > 0 ? ` · ${inTot} opted back in` : '';
  return splitCloses
    ? `Renewing VIPs · ${span} · ${sgn(gainTot)} gained · ${outTxt} · ${sgn(netTot)} net${inTxt}`
    : `Renewing VIPs · ${span} · ${sgn(netTot)} net`;
}

function rangeLabel(from, to) {
  if (!from || !to) return 'Last 7 days';
  const a = from.split('-'), b = to.split('-');
  const aM = MON[Number(a[1]) - 1], bM = MON[Number(b[1]) - 1];
  const aD = Number(a[2]), bD = Number(b[2]);
  return aM === bM ? `${aM} ${aD}-${bD}` : `${aM} ${aD} - ${bM} ${bD}`;
}

// The 7-day net-change bars, ported from renderTrend() in tv.html.
//
// The baseline FLOATS: it sits wherever the split between the best and worst day
// puts it, so both directions share one unit scale (a −2 really is a fifth of a +10)
// without a fixed down-lane crushing the whole series.
function VipTrend({ days }) {
  const W = 700, H = 88, plotTop = 34, plotBot = 66;
  const n = days.length, band = W / n, bw = Math.min(52, band * 0.5);

  let maxUp = 0, maxDn = 0;
  days.forEach((d) => {
    const v = d.delta;
    if (v == null) return;
    if (v > 0) maxUp = Math.max(maxUp, v); else maxDn = Math.max(maxDn, -v);
  });
  const span = maxUp + maxDn;
  const zero = span > 0 ? plotTop + (plotBot - plotTop) * (maxUp / span) : plotBot;
  const unit = span > 0 ? (plotBot - plotTop) / span : 0;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', height: 'auto' }}
         role="img" aria-label="Net change in renewing VIP members per day, last 7 days">
      <line x1="0" y1={zero} x2={W} y2={zero} style={{ stroke: 'var(--gray-300)' }} strokeWidth="2" />
      {days.map((d, i) => {
        const cx = i * band + band / 2, x = cx - bw / 2;
        const missing = d.delta == null;
        // A day the snapshot never recorded. An empty slot with no number: "we did
        // not measure" must never render the same as "nothing happened".
        const ink = missing ? 'var(--gray-300)'
          : d.delta > 0 ? 'var(--navy)' : d.delta < 0 ? 'var(--orange)' : 'var(--gray-500)';
        const lab = missing ? '·'
          : (d.delta > 0 ? '+' : d.delta < 0 ? '−' : '') + Math.abs(d.delta);
        const h = missing ? 0 : Math.max(3, Math.abs(d.delta) * unit);
        const r = Math.min(4, bw / 2, h);
        const up = !missing && d.delta >= 0;
        const y = up ? zero - h : zero + h;
        // Rounded end away from the baseline, square against it — the bar reads as
        // grown from zero rather than floating beside it.
        const bar = up
          ? `M${x} ${zero} V${y + r} Q${x} ${y} ${x + r} ${y} H${x + bw - r} Q${x + bw} ${y} ${x + bw} ${y + r} V${zero} Z`
          : `M${x} ${zero} V${y - r} Q${x} ${y} ${x + r} ${y} H${x + bw - r} Q${x + bw} ${y} ${x + bw} ${y - r} V${zero} Z`;
        return (
          <g key={d.date || i}>
            {missing
              ? <rect x={x} y={zero - 3} width={bw} height="3" rx="1.5" style={{ fill: 'var(--gray-200)' }} />
              : <path d={bar} style={{ fill: ink }} />}
            <text x={cx} y="16" textAnchor="middle" fontSize="20" fontWeight="800" style={{ fill: ink }}>{lab}</text>
            {/* The two halves that make up the net, directly under it so the arithmetic
                reads itself: gained (the change in ACTIVE members) minus the members
                who switched auto-renew off. Shown ONLY on days something actually came
                off — on a clean day the net IS the gain and a second identical number
                is noise. */}
            {d.gained != null && !!d.opted_out && (
              <>
                <text x={cx - 4} y="28" textAnchor="end" fontSize="13" fontWeight="700" style={{ fill: 'var(--gray-700)' }}>
                  {(d.gained > 0 ? '+' : d.gained < 0 ? '−' : '') + Math.abs(d.gained)}
                </text>
                <text x={cx + 4} y="28" textAnchor="start" fontSize="13" fontWeight="700" style={{ fill: 'var(--orange)' }}>
                  {(d.opted_out > 0 ? '−' : '+') + Math.abs(d.opted_out)}
                </text>
              </>
            )}
            {/* Today's label is navy and heavier than the grey weekdays around it.
                That contrast is the only thing marking the live bar. */}
            <text x={cx} y="82" textAnchor="middle" fontSize="15"
                  fontWeight={d.partial ? '800' : '600'}
                  style={{ fill: d.partial ? 'var(--navy)' : 'var(--gray-500)' }}>
              {trendLabel(d.date, !!d.partial)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function VipBoardCard({ vip, error }) {
  const days = vip?.renew_trend || [];
  // Two real days is the floor: one bar is not a trend, it is a number. Caption and
  // chart go together — a heading over an empty strip reads as a chart that broke
  // rather than one that has nothing to say yet.
  const known = days.filter((d) => d && d.delta != null);
  const showTrend = known.length >= 2;

  const rg = vip?.signups_range || {};
  const rgTotal = Number(rg.total || 0);
  const rgCs = Number(rg.cs || 0);

  // The day's movement, taken from the trend's OWN "Today" bar rather than recomputed,
  // so this chip and the last bar of the chart directly above it are physically the
  // same number. It is the net change in RENEWING members, NOT signups - different
  // questions that do not agree.
  const todayBar = days.length && days[days.length - 1] && days[days.length - 1].partial
    ? days[days.length - 1]
    : null;
  const net = todayBar && todayBar.delta != null ? Number(todayBar.delta) : null;
  const sgn = (v) => (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v);

  // ⚠️ COMPOSED ON THE SERVER (buildDaySplit in kiosk-dashboard.js) AND PRINTED
  // VERBATIM. This component used to build the line itself and public/tv.html built the
  // same line again in vanilla DOM; the two drifted three times in five days, each time
  // producing a line that was right on one board and wrong on the other. The arithmetic,
  // the wording and the does-it-close check now live in one place.
  const daySplit = vip?.day_split || '';

  // Members renewing today. Composed on the server (buildRenewalsNote) for the same
  // reason, and deliberately a SEPARATE line from the split: the split has to read as a
  // sum and a renewal moves no count, so it belongs to none of it. Printed at all
  // because a board that says nothing on a day whose only VIP sale was a renewal looks
  // broken - which is how 2026-09-15 played out.
  const renewalsNote = vip?.duplicate_note || '';
  // THE NET CHANGE IN RENEWING MEMBERS FOR THE DAY, composed by the server and printed
  // verbatim - the same string the wall board and the phone print, and the same value as
  // the last bar of the chart above it. Greg, 2026-09-20. It replaces today's SIGNUPS
  // here: a chip reading "+1" above a bar reading "0" for the same day had no way to tell
  // the reader which of the two the tile meant.
  const todayChip = vip?.today_chip || '';

  const label = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.75, marginBottom: 4 };
  const num = { fontSize: 26, fontWeight: 800, lineHeight: 1 };
  const split = { fontSize: 10, fontWeight: 600, opacity: 0.72, marginTop: 5 };

  return (
    <div className="kpi-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="kpi-label">VIP Club</div>

      {error && <div style={{ fontSize: 11, color: 'var(--red)' }}>⚠ {error}</div>}
      {!vip && !error && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading VIP board...</div>}

      {vip && (
        <>
          {showTrend && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-500)', textAlign: 'center', marginBottom: 2 }}>
                {trendCaption(days, known.length)}
              </div>
              <VipTrend days={days} />
            </div>
          )}

          <div className="hero-glass" style={{ marginBottom: 0, padding: 12, marginTop: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="hero-tile" style={{ padding: '10px 12px' }}>
                <div style={label}>Renewing VIPs</div>
                <div style={num}>
                  {fmtN(vip.renewals)}
                  {/* A MEASURED zero now prints "+0 today" rather than going blank, so the
                      day is trackable from 12:01am (Greg, 2026-09-20). An UNMEASURED day
                      still renders nothing: the server sends an empty string for it, and a
                      zero we did not measure is a different claim from nothing moved. */}
                  {todayChip && (
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--peach)', marginLeft: 8 }}>
                      {todayChip}
                    </span>
                  )}
                </div>
                {daySplit && <div style={split}>{daySplit}</div>}
                {renewalsNote && <div style={{ ...split, opacity: 0.62 }}>{renewalsNote}</div>}
              </div>
              <div className="hero-tile" style={{ padding: '10px 12px' }}>
                <div style={label}>Active VIPs</div>
                <div style={num}>
                  {fmtN(vip.active)}
                  {/* Peach, matching the "+N today" chip, because it is the same kind of
                      number: something to go act on. Deliberately NOT red — these are
                      members in good standing who switched auto-renew off, so it is a
                      win-back list, not a failure. */}
                  {vip.not_renewing > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--peach)', opacity: 0.9, marginLeft: 8, whiteSpace: 'nowrap' }}>
                      {fmtN(vip.not_renewing)} not renewing
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* The same split across the window the chart above draws. It runs the FULL
                width of the strip rather than sitting inside the Active tile the way the
                TV has it: a desktop tile is a third of a wall panel, and in there the
                line wrapped mid-phrase. Labelled with the dates it actually covers, not
                "this week" — on a Monday a week-to-date figure is two days and reads
                like a collapse. */}
            {rgTotal > 0 && (
              <div style={{ ...split, marginTop: 8, paddingTop: 7, opacity: 0.62, borderTop: '1px solid rgba(255,255,255,.14)', textAlign: 'center' }}>
                {rangeLabel(rg.from, rg.to)} +{fmtN(rgTotal)} · {fmtN(rg.checkout)} checkout · {fmtN(rgCs)} CS {rgCs === 1 ? 'win' : 'wins'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
